export type PillarArticle = {
  slug: string;
  title: string;
  description: string;
  category: string;
  image: string;
  imageAlt: string;
  author: string;
  publishedAt: string;
  updatedAt: string;
  readingTime: string;
  intro: string[];
  definition: { title: string; paragraphs: string[] };
  facts: Array<{ value: string; label: string; note: string }>;
  comparison: { title: string; intro: string; headers: string[]; rows: string[][] };
  decision: { title: string; intro: string; headers: string[]; rows: string[][]; checklistTitle: string; checklistItems: string[] };
  sections: Array<{ title: string; paragraphs: string[]; bullets?: string[] }>;
  mistakes: Array<{ title: string; body: string }>;
  checklist: { title: string; intro: string; items: string[] };
  faq: Array<{ question: string; answer: string }>;
  internalLinks?: Array<{ label: string; href: string; description: string }>;
  relatedService: string;
  relatedServiceLabel: string;
  sources?: Array<{ label: string; url: string }>;
};

export const pillarArticles: PillarArticle[] = [
  {
    slug: 'hong-kong-whatsapp-chatbot-guide',
    title: 'WhatsApp Chatbot 開發收費攻略｜香港訂製價錢｜渡橋',
    description: '香港 WhatsApp Chatbot 開發收費與訂製價錢攻略：拆解自動回覆、預約、人工轉接、API 整合、測試與持續費用。',
    category: 'WhatsApp Chatbot 開發',
    image: '/images/blog-whatsapp-chatbot-pricing.webp',
    imageAlt: 'WhatsApp Chatbot 開發 規劃工作桌，展示手機對話、預約日曆與人工交接流程',
    author: '渡橋小編',
    publishedAt: '2026-08-14',
    updatedAt: '2026-08-16',
    readingTime: '約 14 分鐘',
    intro: ['問「WhatsApp Chatbot 開發幾多錢」時，最容易被一個機械人名稱誤導。有人只需要自動回答營業時間和服務範圍；有人要先收集地點、服務類型和客戶資料；也有人需要預約、改期、人工交接，再連接日曆或 CRM。功能名稱相同，實際對話和整合工作可以相差很遠。', '因此，比較 WhatsApp Chatbot 開發報價時，不應只看一個總價或每月費用。先確認哪些問題要自動回答、甚麼情況必須交給真人、預約規則是否清楚，以及現有 WhatsApp Business、日曆或 CRM 是否真的可接駁，才知道不同方案是否在比較同一範圍。'],
    definition: { title: 'WhatsApp Chatbot 開發收費，實際買到甚麼？', paragraphs: ['WhatsApp Chatbot 開發不是只寫幾句自動回覆，而是把客戶常問問題、資料收集、預約規則、真人接手和內部通知整理成可測試的對話流程。工作可包括流程梳理、對話文案、選單、資料欄位、預約條件、人工轉接、平台配置、測試、上線和後續更新。', '不是每間公司都需要一開始接 CRM 或做複雜 AI 對話。若客戶問題不多、同事能及時回覆，先整理 FAQ 或使用簡單自動回覆可能已經足夠；當重複查詢、預約來回確認或資料交接已佔去人手，才值得評估訂製流程。'] },
    facts: [{ value: '按範圍', label: '訂製報價方式', note: '按對話、預約、人工轉接、整合、測試和上線安排確認' }, { value: '3 層', label: '常見處理方法', note: '人工回覆、基礎自動回覆、訂製 Chat Bot 各有適用情境' }, { value: '另列', label: '持續第三方費用', note: 'WhatsApp Business API、雲端、日曆、CRM 或其他平台按需要確認' }],
    comparison: { title: '人工回覆、基礎自動回覆與訂製 Chat Bot，怎樣選擇？', intro: '三種方法沒有絕對好壞。重點是重複查詢量、預約複雜度、團隊回覆能力，以及是否需要把資料交給日曆、CRM 或其他系統。', headers: ['做法', '適合情況', '成本結構', '限制與取捨'], rows: [['人工回覆', '查詢量不高、問題複雜或每宗都需專業判斷', '主要是內部人手時間', '速度、說法和資料紀錄較依賴當值同事'], ['基礎自動回覆', '營業時間、基本 FAQ、歡迎訊息或簡單分流', '平台功能或較小設置範圍', '較難處理多步驟預約、例外規則或有用的資料交接'], ['訂製 Chat Bot', '重複問題、預約、資格篩選和人工轉接流程已較明確', '按流程設計、配置、整合、測試及上線範圍報價', '前期要確認規則、帳戶權限與例外情況，不應承諾所有對話完全自動化']] },
    decision: { title: '第一階段應該先做自動回覆、預約還是 CRM 整合？', intro: '不要把所有想法放進第一份報價。先選一條最常出現、最容易漏答或最影響預約的對話流程；上線後再按真實查詢決定是否擴大。', headers: ['目前問題', '第一階段優先處理', '後續可擴充'], rows: [['同事每天回覆相同問題', '服務範圍、營業時間、地點、準備資料和基礎 FAQ', '服務分流、不同語言、優惠或活動流程'], ['預約反覆來回確認', '可約條件、資料收集、確認、改期和真人接手', '日曆同步、提醒、輪班或多人員安排'], ['查詢資料無法跟進', '客戶資料、服務需求、標籤和通知方式', 'CRM、ERP、報表、廣告或網站來源資料串接']], checklistTitle: '索取 Chat Bot 報價前，先回答四個問題', checklistItems: ['哪三至五個問題最常由同事重複回覆？', '客戶預約前必須提供甚麼資料，哪些情況不可自動確認？', '甚麼情況必須由真人接手，由哪位同事負責？', '現有 WhatsApp Business、日曆或 CRM 是否有管理權限及可用 API？'] },
    sections: [{ title: '影響 WhatsApp Chatbot 開發收費的五個因素', paragraphs: ['訂製工作不是按訊息數量收費，而是按機械人需要承接的流程複雜度確認。以下項目會直接影響梳理、配置、開發、測試和上線安排。'], bullets: ['對話流程與語言：服務問題、選單分流、繁中或英文內容，以及需要處理的例外情況', '資料收集與預約：地點、服務類型、人數、時段、緩衝時間、確認、改期和取消規則', '人工轉接：觸發條件、通知對象、已收集資料、聊天紀錄和團隊跟進方法', '第三方整合：WhatsApp Business API、日曆、CRM、雲端或其他工具的帳戶、介面和資料格式', '測試與後續迭代：錯誤輸入、超時、改期、人工接手、上線檢查及新 FAQ 或流程擴充'] }, { title: '一份清楚的 Chat Bot 報價應該寫明甚麼？', paragraphs: ['只寫「WhatsApp Chatbot 開發」和一個總價，日後最容易出現理解落差。報價應讓你知道機械人負責到哪裡、真人從哪裡開始，以及哪些平台費用或資料準備不包括在內。'], bullets: ['第一階段要處理的 FAQ、服務分流、資料收集和預約流程', '對話文案、語言版本、修改次數及客戶提供資料的責任', '人工轉接規則、通知對象、對話歷史和跟進資料的處理方式', '日曆、CRM、ERP 或其他 API 的介面、權限、測試和失敗處理範圍', 'WhatsApp Business API、雲端、日曆、CRM、短訊或其他第三方持續費用', '測試情境、上線檢查、使用說明和後續功能如何另行確認'] }, { title: 'Chat Bot、網站與 ERP 應該怎樣分工？', paragraphs: ['網站或 Landing Page 負責說明服務、信任資料和正式查詢入口；WhatsApp Chatbot 開發可讓機械人先回答重複問題、收集資料、安排預約和轉交真人；ERP 或 CRM 則在需要時整理後續客戶、報價、訂單或營運紀錄。', '例如廣告帶來 WhatsApp 查詢時，先要有與廣告訊息一致的服務頁；當客戶進入對話後，Chat Bot 才按規則收集資料和分流。若團隊隨後仍要在多個表格複製客戶和預約資料，再評估 CRM 或 ERP 整合會較實際。'] }],
    mistakes: [{ title: '把所有問題都交給機械人', body: '複雜報價、投訴、例外服務和關係維護仍需真人判斷。先定義機械人不能處理的情況，體驗通常較穩定。' }, { title: '未整理預約規則便接日曆', body: '沒有先確認服務時間、緩衝、取消、改期和負責人，日曆同步只會把混亂自動化。' }, { title: '只比較月費，不比較帳戶和 API 費用', body: '平台、訊息、雲端、CRM 或日曆費用可能獨立產生。正式報價應分開列出訂製工作和持續第三方成本。' }],
    checklist: { title: 'WhatsApp Chatbot 開發報價準備清單', intro: '不需要先寫好技術規格，但以下資料愈清楚，第一階段範圍愈容易確認。', items: ['目前 WhatsApp 最常見的客戶問題與舊對話例子', '服務範圍、營業時間、地點、價格方向和不承接的情況', '預約所需資料、可約規則、改期及取消做法', '需要真人接手的情況、負責同事和回覆時段', '現有 WhatsApp Business、Meta、日曆、CRM 或 ERP 帳戶與管理權限', '希望先解決的問題、上線時間和預算方向'] },
    faq: [{ question: '香港 WhatsApp Chatbot 開發幾多錢？', answer: '沒有適合所有公司的固定價。渡橋會按對話流程、語言、資料收集、預約、人工轉接、第三方整合、測試和上線支援確認範圍後報價。' }, { question: '基礎自動回覆和訂製 Chat Bot 有甚麼分別？', answer: '基礎自動回覆適合歡迎訊息和簡單 FAQ；訂製 Chat Bot 可按業務規則處理多步驟資料收集、預約、真人轉接和可行的系統整合。' }, { question: 'Chat Bot 是否一定要接 CRM？', answer: '不一定。若團隊只需先處理 FAQ 和預約，未必需要 CRM。需要多人跟進、統一客戶資料或後續報價訂單時，才評估是否值得整合。' }, { question: '預約功能包括 Google Calendar 嗎？', answer: '要視現有帳戶、日曆規則和 API 可用性而定。正式報價會確認是否包含同步、可約時段、改期、提醒及測試；第三方費用會另列。' }, { question: 'WhatsApp Chatbot 開發可以處理人工轉接嗎？', answer: '可以。可按關鍵詞、客戶選擇、複雜程度或預約結果轉交指定同事，並附上已收集的資料；實際通知和交接方式會在範圍內確認。' }, { question: '客戶已有 WhatsApp Business 帳戶怎樣處理？', answer: '會先檢查號碼、Meta Business、帳戶權限和 API 資格，再判斷是否可連接、遷移或保留現有做法。' }, { question: '上線後可以繼續加 FAQ 或功能嗎？', answer: '可以。先上線最常用流程，再按真實對話補充資料收集、預約、通知或系統整合，通常比一次做齊所有功能更容易控制風險。' }],
    internalLinks: [{ label: 'WhatsApp Chatbot 開發服務', href: '/services/whatsapp-chatbot-development', description: '瞭解自動回覆、預約、人工轉接和第三方整合如何按流程規劃。' }, { label: 'Google / Meta 廣告 Landing Page', href: '/services/google-ads-landing-page', description: '讓廣告訊息與網站服務頁、WhatsApp 查詢入口保持一致。' }, { label: 'ERP 營運系統訂製', href: '/services/erp-system-development', description: '當後續客戶、報價、訂單或營運資料需要統一管理時，評估系統化流程。' }, { label: '渡橋價錢詳情', href: '/pricing', description: '查看網站與廣告代營運的報價原則，並瞭解按需求訂製服務的範圍說明。' }],
    relatedService: '/services/whatsapp-chatbot-development',
    relatedServiceLabel: '瞭解 WhatsApp Chatbot 開發服務',
  },
  {
    slug: 'hong-kong-erp-system-pricing-guide',
    title: '香港 ERP 系統收費攻略：營運系統訂製幾多錢？',
    description: '拆解香港 ERP 系統訂製的報價方式、成本因素、Excel／SaaS／訂製 ERP 分別，以及索取營運系統報價前要準備甚麼。',
    category: 'ERP 營運系統',
    image: '/images/blog-erp-system-pricing.webp',
    imageAlt: '營運系統規劃工作桌，展示 ERP 儀錶板、報價資料、員工更表和庫存文件',
    author: '渡橋小編',
    publishedAt: '2026-08-16',
    updatedAt: '2026-08-16',
    readingTime: '約 15 分鐘',
    intro: [
      '搜尋「香港 ERP 系統收費」時，最難得到一個可以直接比較的數字。原因是 ERP 並不是一件固定產品：有人只想把客戶、報價和訂單放進同一個系統；有人還要處理庫存、項目、員工出勤、更表、出糧資料、審批和報表。功能看似同名，實際流程、角色和資料量可以相差很遠。',
      '因此，營運系統訂製不宜只問「幾多錢」。更有效的做法是先確認哪一個流程最浪費人手、哪些資料要共用、誰需要審批，以及日後是否要接駁會計、考勤裝置、短信、電郵或其他 API。這篇攻略會把報價邏輯拆開，讓你知道一份 ERP 報價應該如何閱讀和比較。',
    ],
    definition: {
      title: 'ERP 系統訂製收費，實際買到甚麼？',
      paragraphs: [
        'ERP／營運系統訂製的核心，不是買一個通用後臺，而是把你公司每日重複處理的流程整理成一套可追蹤、可交接、可按角色使用的系統。一般工作包括流程梳理、功能範圍、資料欄位、權限、原型、開發、測試、上線和後續功能迭代。',
        '不是每間公司都需要一開始做大型 ERP。若問題只是一份表格未整理好，先統一欄位和工作規則可能更合適；當同一份客戶、訂單或員工資料被多人重複輸入，交接靠 WhatsApp 或個人記憶，才值得優先評估系統化。',
      ],
    },
    facts: [
      { value: '按需求', label: 'ERP 報價方式', note: '按流程、模組、角色、整合和資料遷移範圍確認' },
      { value: '2 類', label: '常見起步方向', note: '銷售營運流程，或員工管理流程；可分階段開發' },
      { value: '另列', label: '第三方費用', note: '雲端、短信、支付、會計、考勤設備、授權及 API 視需要確認' },
    ],
    comparison: {
      title: 'Excel、現成 SaaS 與訂製 ERP，哪一種較適合？',
      intro: '三種做法沒有絕對高低，重點是目前的流程複雜度、內部管理能力和將來是否需要跨部門共用資料。比較時要同時看初始成本、長期人手和可改動程度。',
      headers: ['方式', '適合情況', '成本結構', '限制與取捨'],
      rows: [
        ['Excel／共享表格', '流程簡單、使用人數少、欄位和審批不常改變', '主要是內部人手時間', '版本容易分散，權限、提醒和交接較依賴人手'],
        ['現成 SaaS', '流程接近標準做法，需要較快上線', '月費、帳戶、插件或設定費', '要配合平台既有功能，複雜流程或本地習慣未必能完整處理'],
        ['訂製 ERP', '跨部門流程、資料欄位、角色權限或整合需求明確', '按規劃、開發、測試、上線及迭代範圍報價', '前期要花時間釐清需求，應由最重要的流程開始，不宜一次做完所有想法'],
      ],
    },
    decision: {
      title: 'ERP 第一階段，應先做銷售還是員工管理？',
      intro: '不要把所有功能一次放進第一份報價。先選一條每天最常出現、最容易出錯或最影響交接的流程，系統上線後再根據實際使用擴充，通常較容易控制預算和風險。',
      headers: ['目前問題', '第一階段可優先處理', '後續可擴充'],
      rows: [
        ['客戶資料、報價與訂單散落多處', '客戶、報價、訂單、狀態和跟進記錄', '庫存、採購、付款狀態、工作排程和管理報表'],
        ['員工出勤與更表靠人手核對', '員工檔案、出勤、更表、請假、加班與審批記錄', '出糧資料整理、匯出、部門報表和考勤裝置整合'],
        ['項目進度和責任人不清楚', '項目、任務、交接、附件和審批節點', '客戶入口、供應商資料、成本追蹤和跨系統通知'],
      ],
      checklistTitle: '決定第一階段前，先確認四件事',
      checklistItems: ['哪一個流程每天使用，並且最常出現重複輸入或漏跟進', '哪些角色需要查看、修改、審批或匯出資料', '現有 Excel、SaaS 或紙本記錄有哪些資料需要保留', '上線後由誰負責使用培訓、資料核對與收集下一階段需要'],
    },
    sections: [
      {
        title: '影響 ERP 系統訂製價錢的五個因素',
        paragraphs: ['ERP 報價不是按「幾多頁」計算，而是按系統要承接的營運複雜度確認。以下五項會直接影響規劃、開發、測試和上線所需的工作。'],
        bullets: ['核心流程與模組：客戶、報價、訂單、庫存、項目、員工、出勤、更表、出糧資料、審批或報表要處理哪些環節', '角色與權限：不同部門、主管和前線人員可以查看、修改、審批或匯出的資料是否不同', '資料遷移：舊 Excel、現有軟件或紙本資料的欄位、重複紀錄、歷史資料和清理需要', '第三方整合：會計、支付、短信、電郵、考勤裝置、雲端或既有 API 是否有可用介面及測試需求', '上線與迭代：培訓、試行、上線支援、後續模組、報表和流程調整的安排'],
      },
      {
        title: '一份清楚的 ERP 報價，應該交代甚麼？',
        paragraphs: ['只寫「ERP 開發」和一個總價，日後最容易出現理解落差。報價應讓你知道第一階段解決哪個問題、包括哪些角色和資料，以及哪些工作或第三方費用不包括在內。'],
        bullets: ['第一階段流程圖、功能模組、資料欄位和使用角色', '原型或操作流程確認方式，以及修改和驗收安排', '資料匯入、清理、歷史紀錄保留和核對責任', '測試、培訓、上線支援和帳戶／資料交接範圍', '雲端、短信、支付、會計、考勤設備、授權和 API 等第三方費用', '後續功能迭代如何提出、確認範圍和另行報價'],
      },
      {
        title: 'ERP、網站、SEO 和廣告，應該怎樣分工？',
        paragraphs: ['網站、SEO 和 Google／Meta 廣告的工作，是讓潛在客戶瞭解服務並發出查詢；ERP 則處理查詢進入公司之後的客戶資料、報價、訂單、工作分配、員工安排與跟進。兩者可以連接，但不應混成同一個預算或同一個交付承諾。', '例如，廣告帶來 WhatsApp 查詢時，先要有清楚的 Landing Page 和轉化追蹤；當查詢量增加、報價和跟進開始靠人手複製資料，才可評估是否把合適資料接入營運系統。先把每個工具的職責分清，報價與成效判斷會更準確。'],
      },
    ],
    mistakes: [
      { title: '一次要求做齊所有部門功能', body: '功能愈多不代表第一階段愈有用。未釐清流程便同時做銷售、庫存、人事、會計和報表，容易令範圍膨脹和測試困難。' },
      { title: '只看開發總價，不看資料與權限', body: '資料遷移、角色權限、審批規則和第三方整合常常決定真正工作量。比較報價時，應先確認各方案是否處理同一範圍。' },
      { title: '以為系統上線後流程自然會改善', body: '系統只會放大既有做法。上線前需要確認資料標準、負責人、交接和培訓，否則只是把混亂流程搬到新工具。' },
    ],
    checklist: {
      title: '索取 ERP 報價前準備清單',
      intro: '不需要先寫好完整規格書，但以下資料愈具體，越容易確認第一階段的合理範圍。',
      items: ['目前最想改善的一至兩條流程，以及每一步由誰負責', '現在使用的 Excel、SaaS、考勤設備、會計或其他工具', '客戶、訂單、員工、出勤或庫存資料目前存放的位置', '需要查看、修改、審批和匯出資料的角色', '必須保留的歷史資料、文件、報表或法定紀錄', '希望先上線的功能、時間方向和可接受的預算範圍'],
    },
    faq: [
      { question: '香港訂製 ERP 系統幾多錢？', answer: 'ERP 沒有適用所有公司的固定價錢。渡橋按流程、模組、使用角色、資料遷移、第三方整合、測試和上線安排確認範圍後報價。先釐清第一階段要解決的問題，比先比較一個總價更有意義。' },
      { question: '可否只先做銷售、報價和訂單？', answer: '可以。對很多中小企來說，先把客戶、報價、訂單、狀態和跟進記錄整理好，已可減少大量重複輸入。庫存、項目、付款或報表可按實際使用情況在後續階段擴充。' },
      { question: 'ERP 可以管理員工、更表和出糧嗎？', answer: '可以按需要處理員工檔案、出勤、更表、請假、加班、出糧資料、審批和匯出。法定薪酬、稅務、強積金及會計處理規則，應由你的 HR、會計師或相關專業人士確認。' },
      { question: '已有 Excel 或現成軟件，是否可以匯入資料？', answer: '可以先檢視來源格式、欄位、重複資料、歷史紀錄和要保留的文件，再確認清理、匯入和驗證範圍。資料品質和現有工具的匯出能力會影響工作量。' },
      { question: '第三方雲端、短信或 API 費用是否包括在 ERP 報價內？', answer: '這些服務通常按供應商、使用量、帳戶或介面另行收費。正式報價會把相關需要列出，避免把系統開發費與持續第三方費用混在一起。' },
      { question: 'ERP 要做幾耐才可以上線？', answer: '時間取決於第一階段流程是否清楚、資料是否準備好、整合難度及測試安排。先以一至兩條核心流程確認範圍，再安排原型、開發、測試和上線，通常比一開始承諾所有功能更可靠。' },
      { question: 'ERP 可以連接網站、SEO 或 Google Ads／Meta 廣告嗎？', answer: '可以按需要評估資料串接，但它們的角色不同：網站與推廣負責帶來查詢，ERP 負責整理客戶、報價、訂單及內部營運資料。應先確認哪一類資料、由哪個系統為主，以及整合是否有可用 API。' },
      { question: '需要先有完整需求文件才可以問價嗎？', answer: '不需要。你可以先提供目前流程、常用表格、涉及角色和最常出錯的環節；我們會協助拆出第一階段需要確認的功能、資料和交接方式，再作報價。' },
    ],
    internalLinks: [
      { label: 'ERP 營運系統訂製服務', href: '/services/erp-system-development', description: '瞭解流程梳理、原型、開發、測試、上線與後續系統迭代。' },
      { label: '渡橋價錢詳情', href: '/pricing', description: '查看網站服務起步價與 ERP 按需求報價的原則。' },
      { label: '全部服務列表', href: '/services', description: '比較網站設計、SEO、廣告與營運系統的服務分工。' },
      { label: '香港網頁設計價錢全攻略', href: '/blog/hong-kong-website-design-pricing-guide', description: '瞭解網站製作與營運系統在目標、範圍和報價上的分別。' },
      { label: 'Google 廣告收費香港攻略', href: '/blog/hong-kong-google-ads-pricing-guide', description: '瞭解廣告流量、Landing Page 與後續營運資料應如何分工。' },
    ],
    relatedService: '/services/erp-system-development',
    relatedServiceLabel: '瞭解 ERP 營運系統訂製服務',
  },
  {
    slug: 'hong-kong-website-design-pricing-guide',
    title: '香港網頁設計價錢全攻略：網站製作幾錢先合理？',
    description: '拆解香港網頁設計價錢、單頁網站與公司網站收費、報價包含項目，以及如何比較不同網站製作公司。',
    category: '網頁設計',
    image: '/images/blog-web-design-pricing.webp',
    imageAlt: '香港網頁設計工作桌，展示網站架構、報價表和設計工具',
    author: '渡橋小編',
    publishedAt: '2026-06-18',
    updatedAt: '2026-08-08',
    readingTime: '約 12 分鐘',
    intro: [
      '搜尋「香港網頁設計價錢」時，你可能會看到幾千元到十幾萬元的報價。差距大，不一定代表其中一方開價離譜，而是大家所說的「做網站」可能根本不是同一件事。',
      '一個只更換模板文字的單頁網站，與需要內容規劃、服務頁、手機版設計、SEO 基礎和後臺管理的公司網站，工作量完全不同。這篇攻略會把報價拆開，讓你知道網站製作幾錢才算符合自己的需要。',
    ],
    definition: {
      title: '甚麼是網頁設計收費？',
      paragraphs: [
        '網頁設計收費不只是購買一個版面。完整工作通常包括瞭解業務、規劃網站架構、整理內容、設計手機與桌面介面、開發功能、設定基本 SEO，以及上線前測試。',
        '報價最重要的不是總共有多少個項目，而是每一項是否對應你的業務目標。如果網站只需要介紹一項服務，未必需要大型 CMS；如果日後要持續增加案例和文章，後臺管理就可能值得投資。',
      ],
    },
    facts: [
      { value: '$999 起', label: '單頁網站', note: '適合單一服務、活動或廣告落地頁' },
      { value: '$3,999 起', label: '公司網站 + CMS', note: '適合需要完整服務架構及後臺更新的中小企' },
      { value: '按需求報價', label: 'ERP 營運系統訂製', note: '按流程、功能、角色、整合和資料遷移範圍報價' },
    ],
    comparison: {
      title: '渡橋方案與香港公開價目怎樣比較？',
      intro: '以下把渡橋首頁公開起步價，與香港常見的網站製作預算方向放在一起。市場價格會按頁數、CMS、文案和修改範圍改變，不能只看一個起步數字；但可以先看出不同預算能做到的範圍。',
      headers: ['類型', '適合對象', '公開市場參考', '渡橋首頁價錢'],
      rows: [
        ['單頁網站', '新服務、短期推廣、測試市場', '市場入門方案通常由數千元起，範圍多由頁面和內容深度決定', '$999 起'],
        ['公司網站 + CMS', '本地中小企、專業服務公司', '多頁企業網站通常由數千至數萬元不等，功能越多差距越大', '$3,999 起'],
        ['ERP 營運系統訂製', '流程和資料分散、需要角色或審批管理', '通常按功能模組、整合和資料遷移範圍報價', '按需求報價'],
      ],
    },
    decision: {
      title: '自己做網站 vs 找網站公司：怎樣判斷？',
      intro: '不是每個項目都一定要找外判。真正要比較的是內部是否有人能處理內容、技術和上線後的問題，而不是只看初始製作費。',
      headers: ['比較項目', '自己處理', '找網站公司'],
      rows: [['內容規劃', '自己訪問團隊、整理服務和頁面次序', '協助把服務、FAQ、信任資料和 CTA 排成網站結構'], ['製作時間', '較依賴內部人手和學習時間', '按已確認範圍安排製作與交付'], ['技術與上線', '自行處理寄存、手機版、SEO 和追蹤', '在報價範圍內處理基本設定與上線檢查'], ['適合情況', '已有懂內容和技術的內部團隊', '想把方向、製作和查詢路徑一併處理']],
      checklistTitle: '決定前先問自己四件事',
      checklistItems: ['是否有人能持續整理服務內容和圖片', '是否需要在指定時間內上線', '是否要處理 SEO、廣告或查詢追蹤', '日後是否會增加服務、案例、文章或語言版本'],
    },
    sections: [
      {
        title: '影響網站製作價錢的六個因素',
        paragraphs: ['同樣是五頁網站，內容深度和功能可以相差很遠。要求報價前，先整理以下範圍，才能得到可比較的數字。'],
        bullets: ['頁面數量，以及每頁是否需要不同版型', '文案由客戶提供、協助整理，還是由零開始撰寫', '圖片、攝影、插圖或品牌素材是否已準備', '是否需要 CMS、多語言、預約、付款或會員功能', 'SEO 關鍵字、頁面標題、結構化資料與追蹤設定', '上線後的寄存、保養、內容更新和技術支援'],
      },
      {
        title: '點解我哋渡橋可以將價錢做到咁平？',
        paragraphs: [
          '渡橋工作室成立不久，現階段仍在累積更多公開案例和長期合作，所以先推出嘗鮮價，讓香港中小企可以用較低門檻試做一個真正能用的網站。單頁方案由 $999 起，先集中做好一項服務、CTA、基本 SEO 和 WhatsApp 查詢。',
          '嘗鮮價不代表把基本交付刪走：手機版、內容整理、基本 SEO、寄存和上線檢查仍會按方案列明。由於每個項目的資料和要求不同，$999 是起步價；新增頁面、語言、CMS 或特殊功能會在報價時逐項確認。',
        ],
      },
      {
        title: '一份清楚的網頁設計報價應該包含甚麼？',
        paragraphs: ['報價應該讓你知道「會做甚麼、何時完成、哪些不包括」。只有一個總價而沒有範圍，日後最容易出現理解落差。'],
        bullets: ['網站頁面和功能清單', '內容、圖片和翻譯由誰負責', '設計與修改流程', '桌面、平板和手機測試', '網域、寄存、SSL 與第三方費用', 'SEO、分析工具和查詢追蹤範圍', '付款階段、預計工期和驗收方式'],
      },
    ],
    mistakes: [
      { title: '只比較首頁外觀', body: '看不到頁面數量、文案、手機版、CMS、SEO 和上線支援，外觀相似不代表交付範圍相同。' },
      { title: '把最低起步價當成最終價', body: '起步價只代表一個清楚範圍；新增語言、功能、內容和頁面都應在正式報價逐項確認。' },
      { title: '網站做好才想流量來源', body: '香港公司網站做完後，流量可來自 SEO、Google Ads、Meta 廣告、社交平台或直接推薦；網站規劃時就要預留相應頁面和追蹤。' },
    ],
    checklist: {
      title: '索取網站報價前準備清單',
      intro: '不需要先寫好所有文案，但以下資料愈清楚，報價愈準確。',
      items: ['你的主要服務和最想推廣的項目', '目標客人和主要服務地區', '現有網站、喜歡的參考網站和不喜歡的做法', '預計頁面、語言和必要功能', '內容、相片、品牌識別的準備情況', '希望上線時間和可接受的預算範圍'],
    },
    faq: [
      { question: '香港做一個網站最少要幾多錢？', answer: '渡橋單頁網站由 $999 起，公司網站 + CMS 由 $3,999 起。市場入門網站通常由數千元起，但頁數、內容和功能不同，不能只比較一個起步數字。渡橋現時以工作室嘗鮮價提供較低門檻，實際範圍仍會在報價中列明。' },
      { question: '網頁設計價錢是否包括網域和寄存？', answer: '不同公司做法不同。網域、寄存、SSL 和第三方服務可能按年收費，應在報價中分開列明，避免把一次性製作費與持續費用混在一起。' },
      { question: '網站做好後是否屬於我？', answer: '應在合約或報價中確認原始內容、設計、程式和帳戶的擁有權，以及能否搬到其他寄存服務。使用封閉平台時亦要了解停止月費後的限制。' },
      { question: '渡橋的 $999 方案是不是只有模板？', answer: '單頁方案會按你的服務、客人和查詢目標整理內容與版面，不是單純貼上模板文字；但它的範圍集中在一頁，若需要多頁 CMS、多語言或複雜功能，就應比較 $3,999 起的公司網站方案或另行報價。' },
      { question: '可以先做一頁，之後再擴充嗎？', answer: '可以。新服務或預算有限時，可先由 $999 起的核心頁面開始，再按搜尋、廣告和客戶問題逐步增加服務頁、CMS 或文章。' },
      { question: '自己做網站和找公司最大的分別是甚麼？', answer: '自己做可節省部分製作費，但要自行處理內容、版面、手機、SEO、追蹤和技術問題；找公司則購買規劃、執行和交付時間。應按內部能力和機會成本選擇。' },
      { question: '香港公司網站做完後，流量通常從哪裡來？', answer: '常見來源包括品牌搜尋、SEO、Google Ads、Meta 廣告、社交平台、地圖和客戶推薦。網站要為主要來源準備合適的服務頁、Landing Page 和查詢追蹤。' },
    ],
    relatedService: '/pricing',
    relatedServiceLabel: '查看渡橋網頁設計價目表',
  },
  {
    slug: 'hong-kong-seo-pricing-guide',
    title: 'SEO 收費香港攻略：幾多錢先合理？',
    description: '瞭解香港 SEO 收費模式、月費與一次性 SEO 分別、合理服務範圍，以及比較 SEO 公司報價時要注意的事項。',
    category: 'SEO',
    image: '/images/blog-seo-pricing.webp',
    imageAlt: '香港 SEO 策略工作桌，展示搜尋數據、關鍵字分類和內容地圖',
    author: '渡橋小編',
    publishedAt: '2026-05-27',
    updatedAt: '2026-07-22',
    readingTime: '約 13 分鐘',
    intro: [
      '香港 SEO 收費可以由一次性幾千元，到每月數萬元不等。問題不是「愈貴愈好」，而是服務範圍是否對應你的網站狀況、競爭程度和業務目標。',
      'SEO 不是買一個指定排名。它包含技術檢查、關鍵字研究、網站架構、服務頁內容、內部連結和持續分析。合理報價應該說明會改善甚麼，而不是只承諾「上首頁」。',
    ],
    definition: {
      title: 'SEO 收費實際買到甚麼？',
      paragraphs: [
        'SEO 收費是為了令搜尋引擎更容易理解網站，並讓有明確需要的人找到合適頁面。工作可分為技術、內容和權威三部分：網站能否被讀取、頁面是否真正回答搜尋問題，以及網站是否有可信的外部訊號。',
        '不是每個項目都要每月做同樣工作。新網站通常先處理關鍵字和架構；舊網站可能先修正重複頁面、速度和內容；競爭較高的行業則需要持續建立有價值的服務頁與支柱文章。',
      ],
    },
    facts: [
      { value: '3–6 個月', label: '常見觀察期', note: '不是排名保證，實際視競爭與網站基礎' },
      { value: '3 大範圍', label: '技術／內容／權威', note: '完整 SEO 不應只改 meta title' },
      { value: '每月檢視', label: 'Search Console 數據', note: '追蹤曝光、點擊、查詢字和頁面變化' },
    ],
    comparison: {
      title: '一次性 SEO、月費 SEO 與 SEO 顧問有甚麼分別？',
      intro: '收費模式要配合工作性質。網站剛重做，可能先需要一次性規劃；要持續競爭服務關鍵字，就需要按月執行與檢視。',
      headers: ['模式', '適合情況', '通常包括', '要留意'],
      rows: [
        ['一次性 SEO', '新站上線、舊站基礎整理', '關鍵字、架構、技術檢查、頁面建議', '完成後仍需有人執行與追蹤'],
        ['月費 SEO', '持續增加內容和競爭排名', '內容優化、技術修正、報告與迭代', '每月交付應具體，不應只有報告'],
        ['SEO 顧問', '已有內部內容或開發團隊', '策略、優先次序、審核和培訓', '執行責任要事先分清'],
      ],
    },
    decision: {
      title: '先做 SEO 還是先做 Google Ads？',
      intro: '兩者可同時進行，但不一定同時投入同樣資源。選擇要看是否急於測試需求，以及網站是否已有可承接流量的服務頁。',
      headers: ['情況', '較適合先做 SEO', '較適合先做 Google Ads'],
      rows: [['網站已有基礎內容', '用服務頁、FAQ 和文章逐步累積搜尋', '用廣告測試哪個服務和訊息較容易帶來查詢'], ['急於取得流量', 'SEO 可同步規劃，但未必是最快來源', '可較快取得曝光和查詢資料'], ['預算使用', '主要投入內容、技術和持續優化', '媒體費和代營運費需要分開看'], ['共同需要', '清楚服務頁、可信內容和查詢追蹤', '清楚服務頁、可信內容和查詢追蹤']],
      checklistTitle: '開始 SEO 前檢查',
      checklistItems: ['每項主要服務是否有對應頁面', '客人搜尋的問題是否已寫進服務頁或 FAQ', 'Google Search Console 是否已設定', '誰負責根據建議更新內容和技術項目'],
    },
    sections: [
      { title: '影響香港 SEO 收費的因素', paragraphs: ['SEO 工作量由市場和網站基礎決定。以下因素會直接影響所需時間。'], bullets: ['目標關鍵字的競爭程度與搜尋意圖', '網站頁面數量、技術平台和現有問題', '是否已有清楚的服務頁與可信內容', '需要撰寫、審核或更新的內容數量', '本地搜尋、地區頁、Google Business Profile 的需要', '團隊能否配合開發、內容與數據追蹤'] },
      { title: '合理 SEO 報價應該如何交代工作？', paragraphs: ['專業報價會先說明現況和優先次序，再列出可執行項目。每月「提交幾百條連結」或大量生產相似文章，不代表能建立長期搜尋價值。'], bullets: ['基礎審核和量度方式', '目標頁面與關鍵字分組', '每月內容或技術交付', 'Search Console 與分析數據檢視', '哪些工作由客戶、顧問或開發團隊負責'] },
      { title: '如何避免只看排名報告？', paragraphs: ['排名可以波動，亦未必等於生意。應同時看非品牌曝光、合適頁面的點擊、查詢字是否接近服務，以及自然搜尋是否帶來電話、WhatsApp 或表單。', '如果流量增加但查詢沒有改善，問題可能在頁面內容、服務定位或 CTA，而不只是繼續追更多關鍵字。'] },
    ],
    mistakes: [
      { title: '只追一個大關鍵字', body: '高搜尋量不一定最接近服務意圖。應把品牌字、服務字、地區字和問題字分配到不同頁面。' },
      { title: '把每月報告當成優化', body: '報告只說明發生甚麼；真正服務還要提出優先修改、執行內容和下一輪檢查。' },
      { title: 'SEO 和廣告完全分開做', body: '兩者節奏不同，但應共用清楚的服務頁、訊息和查詢追蹤。廣告數據亦可協助判斷哪些搜尋問題值得優先做 SEO。' },
    ],
    checklist: { title: '比較 SEO 公司前的檢查清單', intro: '先問清執行方法，再比較月費。', items: ['是否先了解業務、利潤較高的服務和客人問題', '會否提供關鍵字與頁面對應，而非把所有字放首頁', '技術修改和內容製作由誰執行', '報告是否連結到實際頁面和查詢目標', '是否承諾無法控制的指定排名', '停止合作後，內容、帳戶和數據是否仍屬於你'] },
    faq: [
      { question: '香港 SEO 每月收費幾多先合理？', answer: '沒有一個適用所有公司的固定數字。小型網站的基礎顧問或優化，與高競爭行業的內容和技術執行，工作量差距很大。應先比較每月交付、負責範圍和量度方法。' },
      { question: 'SEO 做幾耐先有效？', answer: '一般需要數月觀察搜尋引擎重新理解和評估頁面，但時間受網站歷史、競爭和執行速度影響。任何「幾日上首頁」都不應視為可保證結果。' },
      { question: 'SEO 是否一定要每月付費？', answer: '不一定。網站可以先做一次性架構和頁面優化；如果要持續發布內容、修正技術問題和競爭重要關鍵字，月費模式較容易保持執行節奏。' },
      { question: '有流量但沒有查詢，是否 SEO 無效？', answer: '未必。先檢查流量是否來自合適搜尋意圖，以及落地頁是否清楚交代服務、信任和下一步。SEO 帶人進站，頁面仍要負責轉化。' },
      { question: 'SEO 和 Google Ads 可以同時做嗎？', answer: '可以。Google Ads 可較快測試搜尋字和落地頁，SEO 則累積長期內容。兩者應分開量度成本，但可以共用服務定位、頁面和查詢追蹤。' },
      { question: 'SEO 服務是否包括修改網站？', answer: '要看報價範圍。有些服務只提供建議，有些會直接修改技術、頁面和內容。比較前應確認誰負責執行，以及開發工作是否另行收費。' },
    ],
    relatedService: '/services/seo',
    relatedServiceLabel: '瞭解渡橋 SEO 搜尋優化服務',
  },
  {
    slug: 'hong-kong-google-ads-pricing-guide',
    title: 'Google 廣告收費香港攻略：廣告費與代營運費點計？',
    description: '拆解香港 Google Ads 廣告費、代營運收費、每次點擊成本、預算分配，以及選擇廣告服務時應注意的項目。',
    category: 'Google Ads',
    image: '/images/blog-google-ads-pricing.webp',
    imageAlt: '香港 Google 廣告規劃工作桌，展示預算、轉化漏斗和成效圖表',
    author: '渡橋小編',
    publishedAt: '2026-06-30',
    updatedAt: '2026-07-30',
    readingTime: '約 12 分鐘',
    intro: ['Google 廣告收費通常由兩部分組成：直接支付給平台的廣告費，以及廣告代理或顧問的設定和代營運費。兩者用途不同，報價時應分開看。', '廣告預算不是愈大愈好。關鍵是搜尋字是否對應服務、落地頁能否承接查詢，以及電話、WhatsApp 或表單有沒有正確追蹤。沒有這些基礎，再高的點擊量也很難判斷成效。'],
    definition: { title: 'Google 廣告收費包括甚麼？', paragraphs: ['廣告費是參與搜尋關鍵字競價的媒體預算，通常按點擊扣費；代營運費則涵蓋帳戶結構、關鍵字、廣告文案、轉化追蹤、否定關鍵字和持續優化。', '每次點擊價錢由競爭、廣告品質、地區、時間和落地頁相關性共同影響。代理不能預先固定每次點擊成本，但可以透過更清晰的結構和持續排除無效搜尋，改善預算使用。'] },
    facts: [
      { value: '2 部分', label: '媒體費＋代營運費', note: '報價應分開列明' },
      { value: '按點擊', label: '搜尋廣告常見計費', note: '實際 CPC 由競價與品質決定' },
      { value: '3 類轉化', label: '電話／WhatsApp／表單', note: '至少追蹤最重要的查詢動作' },
    ],
    comparison: { title: '自行投放、顧問設定與全託管有甚麼分別？', intro: '選擇方式取決於團隊時間和經驗，而不是只看代營運費高低。', headers: ['方式', '適合對象', '優點', '風險'], rows: [['自行投放', '預算小、願意學習和每週檢查', '節省管理費、掌握帳戶', '容易忽略追蹤和搜尋字浪費'], ['一次性設定', '內部有人可持續管理', '建立正確結構和追蹤', '後續仍需定期優化'], ['全託管', '希望由專人持續處理', '有人監察、測試和報告', '要確認帳戶擁有權與交付']] },
    decision: {
      title: 'Google Ads 流量應該落首頁還是 Landing Page？',
      intro: '落地頁選錯，廣告即使有點擊也會把客人帶到要重新找資料的地方。頁面要跟搜尋意圖，而不是一律回首頁。',
      headers: ['搜尋或推廣情況', '首頁較合適', '服務頁 / Landing Page 較合適'],
      rows: [['品牌名稱搜尋', '客人想了解整間公司或比較多項服務', '通常不需要另開頁面'], ['指定服務搜尋', '只有首頁已完整交代該服務時才考慮', '客人搜尋的服務、地區、價錢方向和 CTA 應直接出現'], ['短期優惠或活動', '資訊較多、訊息容易分散', '用一頁集中優惠、期限、FAQ 和轉化動作'], ['資料判斷', '看整站瀏覽和不同服務去向', '看指定廣告、頁面和查詢質素']],
      checklistTitle: 'Google Ads 上線前清單',
      checklistItems: ['確認每個 campaign 的唯一服務或目標', '檢查廣告標題和 Landing Page 首屏是否一致', '設定 WhatsApp、電話或表單追蹤', '安排查詢後怎樣記錄地區、需要和成交狀況'],
    },
    sections: [
      { title: 'Google 廣告預算應該點定？', paragraphs: ['先由商業數字倒推，而不是先問每日最低可以放多少。瞭解一次有效查詢的可接受成本、成交率和客戶價值，才知道廣告是否有合理空間。'], bullets: ['選定一至兩項最值得推廣的服務', '估算每月需要多少有效查詢', '設定可接受的每次查詢成本', '預留足夠數據量測試關鍵字與文案', '把品牌字、服務字和競爭較高的字分開管理'] },
      { title: '代營運費應該包含哪些工作？', paragraphs: ['只有每月報告而沒有實際優化，不足以稱為代營運。帳戶應持續檢查搜尋字、否定關鍵字、裝置、地區、時段和轉化資料。'], bullets: ['帳戶與 campaign 結構', '關鍵字、配對方式和否定字', '廣告文案與延伸資訊', 'GA4、標籤和轉化追蹤', '搜尋字與預算分配檢查', '每月結論、下一步測試和落地頁建議'] },
      { title: '點擊平不等於廣告好', paragraphs: ['便宜點擊可能來自意圖較弱的搜尋。專業服務更應關注查詢是否符合服務範圍，而不是單純追求最低 CPC。', '同樣地，轉化數字也要排除垃圾表單、重複點擊和不合適地區。最終應連結到實際查詢質素和成交情況。'] },
    ],
    mistakes: [
      { title: '所有關鍵字共用一個廣告組', body: '不同服務和搜尋意圖混在一起，廣告文案、落地頁和預算都較難配合。' },
      { title: '只看每次點擊價錢', body: '較平的點擊未必帶來合適查詢，還要看搜尋字、地區、轉化和最後的客戶質素。' },
      { title: '廣告流量一律落首頁', body: '搜尋特定服務的人應落到對應服務頁或 Landing Page；品牌搜尋或需要比較多項服務時，首頁才可能較適合。' },
    ],
    checklist: { title: '開始 Google Ads 前準備清單', intro: '這些基礎能減少廣告上線後的浪費。', items: ['清楚的服務範圍、地區和不承接項目', '獨立服務頁或 Landing Page', '可追蹤的電話、WhatsApp 或表單', '廣告帳戶、GA4 和網站管理權限', '每月媒體預算與可接受查詢成本', '負責回覆查詢和記錄質素的人員'] },
    faq: [
      { question: 'Google 廣告每日最低要放幾多錢？', answer: '平台可以用較低預算開始，但預算太少可能難以累積足夠數據。合理金額要視關鍵字點擊成本、服務地區和需要的查詢量，應先由商業目標倒推。' },
      { question: '代營運費是否包括廣告費？', answer: '通常不包括。媒體費直接支付給 Google，代營運費支付給管理帳戶的公司或顧問。報價應分開列出，並確認廣告帳戶由客戶擁有。' },
      { question: '為甚麼有點擊但沒有查詢？', answer: '常見原因包括關鍵字意圖不對、地區設定過闊、落地頁說不清、CTA 不方便或轉化追蹤失效。應逐項檢查，而不是只增加預算。' },
      { question: 'Google Ads 幾耐可以看到結果？', answer: '廣告上線後可以很快獲得曝光和點擊，但找出有效關鍵字、文案和查詢成本需要測試期。初期應避免過早按少量數據作結論。' },
      { question: 'Google Ads 流量應該落到哪一頁？', answer: '特定服務搜尋應落到對應服務頁或 Landing Page；品牌搜尋或要比較多項服務時可使用首頁。廣告標題、搜尋意圖和頁面首屏要保持一致。' },
      { question: '是否需要同時做 SEO？', answer: '不是投放的必要條件，但網站架構、服務頁和追蹤可同時支援 SEO。廣告帶來的搜尋字和查詢資料，亦可幫助判斷 SEO 內容優先次序。' },
    ],
    internalLinks: [
      { label: 'Google Ads 搜尋廣告服務', href: '/services/google-ads', description: '瞭解關鍵字、否定關鍵字、搜尋廣告、Landing Page 和查詢追蹤如何一起規劃。' },
      { label: 'Google / Meta 廣告 Landing Page', href: '/services/google-ads-landing-page', description: '當廣告只推一項服務或優惠時，建立對應頁面承接搜尋流量。' },
      { label: 'Facebook／Meta 廣告收費攻略', href: '/blog/hong-kong-facebook-ads-pricing-guide', description: '比較主動觸及受眾的 Meta 廣告，與承接搜尋需要的 Google Ads 如何分工。' },
      { label: '廣告代營運價錢詳情', href: '/pricing', description: '查看 Google Ads／Meta 廣告代營運按月報價，以及媒體費另計的原則。' },
    ],
    relatedService: '/services/google-ads',
    relatedServiceLabel: '瞭解 Google Ads 搜尋廣告服務',
  },
  {
    slug: 'hong-kong-facebook-ads-pricing-guide',
    title: 'Facebook 廣告收費香港攻略：FB 廣告預算與代操費用',
    description: '瞭解香港 Facebook 廣告收費、Meta 廣告預算、代操費、素材製作和轉化追蹤，判斷怎樣投放才合理。',
    category: 'Facebook Ads',
    image: '/images/blog-facebook-ads-pricing.webp',
    imageAlt: '香港 Facebook 廣告內容規劃工作桌，展示社交媒體素材、受眾和成效數據',
    author: '渡橋小編',
    publishedAt: '2026-07-11',
    updatedAt: '2026-08-03',
    readingTime: '約 12 分鐘',
    intro: ['Facebook 廣告收費不只是一筆 Boost Post 費用。完整成本通常包括支付給 Meta 的媒體預算、帳戶和 campaign 管理、圖片或影片素材，以及 Landing Page 和轉化追蹤。', 'FB 廣告的強項是主動接觸受眾和快速測試訊息，但用戶未必正在搜尋你的服務。因此，素材、受眾和落地頁需要一起規劃，不能只把現有貼文加大曝光。'],
    definition: { title: 'Facebook 廣告收費買到甚麼？', paragraphs: ['媒體費用讓廣告進入 Meta 的競價系統，按展示、點擊或結果分配；代操費則支付策略、受眾設定、素材測試、Pixel/Conversions API、預算調整和報告。', '如果需要持續製作影片、攝影、設計多個廣告版本，素材費也應獨立列明。把所有項目混成一個月費，會很難知道預算到底用在曝光還是製作。'] },
    facts: [
      { value: '3 個核心', label: '受眾／素材／落地頁', note: '任何一項太弱都會限制成效' },
      { value: '多版本', label: '素材測試', note: '比較角度與格式，不是只換顏色' },
      { value: '第一方數據', label: 'Pixel 與事件追蹤', note: '受私隱設定影響，需配合實際查詢記錄' },
    ],
    comparison: { title: 'Boost Post、Ads Manager 與全漏斗投放分別', intro: '三種做法都可以使用，但解決的問題不同。', headers: ['方式', '主要用途', '優點', '限制'], rows: [['Boost Post', '增加貼文互動或觸及', '設定快、操作簡單', '受眾、版位和測試彈性較少'], ['Ads Manager', '查詢、銷售或名單目標', '結構、受眾和追蹤較完整', '需要持續管理和分析'], ['全漏斗投放', '由認識到再營銷', '按用戶階段安排訊息', '需要較多素材、數據和預算'] ] },
    decision: {
      title: 'Facebook 廣告 vs Google 廣告：如何分工？',
      intro: '平台不是替代關係。Google 較接近承接現有需要，Facebook 則較適合主動展示概念、素材和再營銷。',
      headers: ['比較項目', 'Facebook / Meta 廣告', 'Google Ads'],
      rows: [['客人狀態', '未必正在找服務，需要先理解或被吸引', '通常已有問題，正主動搜尋解決方法'], ['內容形式', '圖片、短片、案例和不同訊息角度', '關鍵字、文字廣告和直接服務訊息'], ['頁面需要', '要快速交代概念、信任和下一步', '要直接回應搜尋字和服務需要'], ['常見用途', '新服務曝光、再營銷、素材測試', '服務搜尋、地區需求、較高意圖查詢']],
      checklistTitle: 'Facebook 廣告啟動清單',
      checklistItems: ['把一個廣告組合集中在一個主要目標', '至少準備兩個不同訊息角度，而非只換顏色', '確認手機版 Landing Page 或即時表單可用', '把平台數據與實際查詢質素一起記錄'],
    },
    sections: [
      { title: 'Facebook 廣告總成本由甚麼組成？', paragraphs: ['比較報價時，把以下成本拆開，才能知道不同方案是否真的相同。'], bullets: ['Meta 媒體預算', '策略、帳戶設定與每月代操費', '平面、短片、攝影和文案製作', 'Landing Page 或即時表單', 'Pixel、事件與 Conversions API 設定', '素材更新、再營銷和成效分析'] },
      { title: 'FB 廣告預算應該如何分配？', paragraphs: ['初期應集中測試少量清楚的服務和訊息，不要同時開太多受眾，令每組廣告都沒有足夠數據。預算較小時，一個主要目標通常比同時追求互動、流量和查詢更實際。'], bullets: ['先定一個主要轉化目標', '準備兩至四個真正不同的素材角度', '分清陌生受眾和曾互動受眾', '預留素材更新和落地頁改善空間', '用實際查詢質素回饋平台數據'] },
      { title: '為甚麼很多 Facebook 廣告只有 Like，沒有生意？', paragraphs: ['互動成本低不代表受眾會購買。廣告目標選錯、內容只追求吸睛、服務說法模糊，或點擊後沒有清楚下一步，都會令互動停在平台內。', '對本地服務公司而言，應讓用戶快速瞭解地區、服務範圍、價錢方向和聯絡方法。若需要較長時間建立信任，則要設計再營銷內容，而不是只重複同一個銷售廣告。'] },
    ],
    mistakes: [
      { title: '把 Boost Post 當成完整策略', body: '增加觸及可以有用，但若目標是查詢，仍要處理受眾、轉化事件、落地頁和後續回覆。' },
      { title: '素材只換顏色和尺寸', body: '真正測試應比較不同問題、利益、證明和行動角度，而不是同一訊息的表面變化。' },
      { title: '只相信平台顯示的轉化', body: 'Pixel 和歸因受私隱設定影響，應把平台數據與實際 WhatsApp、電話、表單和成交記錄互相核對。' },
    ],
    checklist: { title: '開始 Facebook 廣告前準備清單', intro: '素材和追蹤準備得愈完整，測試愈有意義。', items: ['一個清楚的服務、優惠或內容主題', '目標客人的情境、問題和主要顧慮', '至少兩個不同訊息角度的素材', '手機易讀的 Landing Page 或表單', 'Pixel、事件和私隱政策設定', '查詢回覆流程與客戶質素記錄'] },
    faq: [
      { question: 'Facebook 廣告每月要幾多預算？', answer: '視乎受眾大小、素材成本和轉化目標。預算應足以讓少量廣告組合累積數據；若預算有限，應減少測試範圍，而不是把金額平均分到大量受眾。' },
      { question: 'FB 廣告代操費包括設計嗎？', answer: '未必。策略和帳戶管理、平面設計、影片拍攝與剪輯通常是不同工作。報價應列明每月提供多少素材、修改次數和素材擁有權。' },
      { question: 'Boost Post 是否等於正式投放廣告？', answer: 'Boost Post 是簡化投放工具，適合快速增加觸及或互動；需要查詢、轉化追蹤、多受眾和素材測試時，Ads Manager 通常更合適。' },
      { question: 'Facebook 廣告與 Google 廣告應該選哪個？', answer: 'Google 適合承接正在搜尋服務的人；Facebook 適合主動建立需求、展示視覺和再營銷。選擇應看客人是否已有明確搜尋意圖，也可以讓兩者分工。' },
      { question: 'Facebook 廣告流量應該落到網站還是即時表單？', answer: '需要快速收集簡單資料可測試即時表單；需要較多解釋、案例和信任內容，應使用手機易讀的 Landing Page。兩者都要記錄實際查詢質素。' },
      { question: '素材多久需要更新一次？', answer: '沒有固定日數。當頻率上升、受眾反應下降、查詢成本變差，或服務訊息改變時便應更新。預算愈大、受眾愈小，素材疲勞通常愈快。' },
    ],
    internalLinks: [
      { label: 'Facebook／Meta 廣告服務', href: '/services/meta-ads', description: '瞭解 FB、IG、Threads 廣告的受眾、素材、再營銷、Meta Pixel 和查詢追蹤。' },
      { label: 'Google / Meta 廣告 Landing Page', href: '/services/google-ads-landing-page', description: '需要交代服務、案例、價錢方向或信任資料時，以手機易讀的 Landing Page 承接流量。' },
      { label: 'Google 廣告收費香港攻略', href: '/blog/hong-kong-google-ads-pricing-guide', description: '瞭解客人主動搜尋服務時，Google Ads 的關鍵字、Landing Page 與預算如何安排。' },
      { label: '廣告代營運價錢詳情', href: '/pricing', description: '查看 Google Ads／Meta 廣告代營運按月報價，以及媒體費另計的原則。' },
    ],
    relatedService: '/services/meta-ads',
    relatedServiceLabel: '瞭解 Facebook / Meta 廣告服務',
  },
];

const legacySlugAliases: Record<string, string> = {
  'hong-kong-web-design-pricing': 'hong-kong-website-design-pricing-guide',
  'seo-pricing-hong-kong': 'hong-kong-seo-pricing-guide',
  'google-ads-pricing-hong-kong': 'hong-kong-google-ads-pricing-guide',
  'facebook-ads-pricing-hong-kong': 'hong-kong-facebook-ads-pricing-guide',
};

export function getPillarArticle(slug: string): PillarArticle | undefined {
  const resolvedSlug = legacySlugAliases[slug] ?? slug;
  return pillarArticles.find((article) => article.slug === resolvedSlug);
}
